import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import React, { useState } from 'react';
import { trpc } from '~/api/trpc';
import { LoginResult } from '../../auth/authContext';
import useAuth from '../../auth/useAuth';
import Logo from '../layout/Logo';

interface FormFields {
    username: string;
    password: string;
}

interface FormErrors {
    username?: string;
    password?: string;
}

const LoginForm = () => {
    const [formData, setFormData] = useState<FormFields>({
        username: '',
        password: ''
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loginError, setLoginError] = useState<string>('');

    const auth = useAuth();
    const trpcUtils = trpc.useUtils();

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.username.trim()) {
            newErrors.username = 'Bitte geben Sie Ihre E-Mail Adresse ein!';
        }

        if (!formData.password.trim()) {
            newErrors.password = 'Bitte geben Sie Ihr Passwort ein!';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: keyof FormFields, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }

        // Clear login error when user starts typing
        if (loginError) {
            setLoginError('');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setLoginError('');

        try {
            const loginResult = await auth.signIn(
                formData.username.toLowerCase(),
                formData.password
            );

            trpcUtils.user.getMe.refetch();

            if (loginResult !== LoginResult.successful) {
                setLoginError('Invalid credentials');
            }
        } catch (error: any) {
            if (error.message) {
                setLoginError(error.message);
            } else {
                setLoginError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
            <Card className="w-full max-w-md shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader className="text-center pb-2">
                    <Logo type='dark' />
                </CardHeader>

                <CardContent className="space-y-6">
                    {loginError && (
                        <Alert variant="destructive" className="animate-in slide-in-from-top-2">
                            <AlertDescription>{loginError}</AlertDescription>
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="username" className="text-sm font-medium text-slate-700">
                                E-Mail Adresse
                            </Label>
                            <div className="relative">
                                <Mail
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4"
                                />
                                <Input
                                    id="username"
                                    type="email"
                                    placeholder="E-Mail Adresse"
                                    value={formData.username}
                                    onChange={(e) => handleInputChange('username', e.target.value)}
                                    className={`pl-10 h-12 transition-all duration-200 ${errors.username
                                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                            : 'focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                                        }`}
                                    autoFocus
                                    disabled={isLoading}
                                />
                            </div>
                            {errors.username && (
                                <p className="text-red-500 text-xs mt-1 animate-in slide-in-from-top-1">
                                    {errors.username}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                                Passwort
                            </Label>
                            <div className="relative">
                                <Lock
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4"
                                />
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Passwort"
                                    value={formData.password}
                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                    className={`pl-10 pr-10 h-12 transition-all duration-200 ${errors.password
                                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                            : 'focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                                        }`}
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                    disabled={isLoading}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                            {errors.password && (
                                <p className="text-red-500 text-xs mt-1 animate-in slide-in-from-top-1">
                                    {errors.password}
                                </p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-12 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center space-x-2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Anmelden...</span>
                                </div>
                            ) : (
                                'Anmelden'
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default LoginForm;